import { clamp } from '../core/math';
import {
  CELL,
  CellState,
  GRID_H,
  GRID_W,
  LOGICAL_H,
  LOGICAL_W,
  N_CELLS,
  PKind,
  PState,
  T,
  TERRAIN,
  idx,
} from '../sim/types';
import { EState, HOSE_REACH, engineCell } from '../sim/engine';
import { DROP_DRIFT, DROP_FALL, bucketAnchor, bucketInWater } from '../sim/heli';
import type { World } from '../sim/world';
import {
  ACEQUIA,
  BLUE,
  BURNT,
  BUILDING,
  FIRE,
  GRASS,
  OLIVE,
  OLIVE_LINE,
  PAD,
  PINE,
  RED,
  ROAD,
  ROCK,
  SCRUB,
  SKY_LOW,
  SKY_MID,
  SKY_TOP,
  SMOKE_SKY,
  WATER,
  blend,
} from './palette';
import {
  BASE_MARK,
  BUCKET,
  CARS,
  FIRE_ENGINE,
  FIRE_ENGINE_LOST,
  HELI,
  HELI_L,
  HOUSE,
  HOUSE_BURNT,
  PAD_MARK,
  Sprite,
} from './sprites';

const STREAKS = 46;

function hash(i: number, frame: number): number {
  let h = (Math.imul(i, 374761393) + Math.imul(frame, 668265263)) | 0;
  h = Math.imul(h ^ (h >>> 13), 1274126177) | 0;
  return ((h ^ (h >>> 16)) >>> 0) / 4294967296;
}

export class Renderer {
  private readonly buf: Uint32Array;
  private readonly img: ImageData;
  private readonly layer: HTMLCanvasElement;
  private readonly lctx: CanvasRenderingContext2D;
  private readonly streaks: Float32Array;

  constructor(private readonly ctx: CanvasRenderingContext2D) {
    const bytes = new Uint8ClampedArray(N_CELLS * 4);
    this.buf = new Uint32Array(bytes.buffer);
    this.img = new ImageData(bytes, GRID_W, GRID_H);
    this.layer = document.createElement('canvas');
    this.layer.width = GRID_W;
    this.layer.height = GRID_H;
    this.lctx = this.layer.getContext('2d')!;
    this.streaks = new Float32Array(STREAKS * 3);
    for (let i = 0; i < STREAKS; i++) {
      this.streaks[i * 3] = Math.random() * LOGICAL_W;
      this.streaks[i * 3 + 1] = Math.random() * LOGICAL_H;
      this.streaks[i * 3 + 2] = 4 + Math.random() * 7;
    }
  }

  draw(w: World, time: number): void {
    const ctx = this.ctx;
    ctx.imageSmoothingEnabled = false;
    this.paintGround(w, time);
    this.lctx.putImageData(this.img, 0, 0);
    ctx.drawImage(this.layer, 0, 0, GRID_W, GRID_H, 0, 0, LOGICAL_W, LOGICAL_H);

    this.drawWindStreaks(w, time);
    this.drawPads(w);
    this.drawHouses(w);
    this.drawCars(w);
    this.drawEngines(w, time);
    this.drawPeople(w, time);
    this.drawDrops(w);
    this.drawParticles(w);
    this.drawHeli(w, time);
    this.drawAimedObjective(w, time);
  }

  /** Where the armed order would land, highlighted under the cursor. */
  aim: { x: number; y: number; label: string; ok: boolean } | null = null;

  private drawAimedObjective(w: World, time: number): void {
    const a = this.aim;
    if (!a || w.selected < 0) return;
    const ctx = this.ctx;
    const px = a.x * CELL + CELL / 2;
    const py = a.y * CELL + CELL / 2;
    ctx.strokeStyle = a.ok ? '#ffff00' : '#ff0000';
    ctx.globalAlpha = 0.55 + 0.25 * Math.sin(time * 6);
    ctx.setLineDash([3, 3]);
    ctx.beginPath();
    ctx.ellipse(px, py, 9, 7, 0, 0, Math.PI * 2);
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.beginPath();
    ctx.moveTo(px - 12, py);
    ctx.lineTo(px - 6, py);
    ctx.moveTo(px + 6, py);
    ctx.lineTo(px + 12, py);
    ctx.stroke();
    ctx.globalAlpha = 1;
  }

  // -------------------------------------------------------------------------

  private paintGround(w: World, time: number): void {
    const { grid, mtn } = w;
    const { terrain, state, heat, moist, inten, tex } = grid;
    const haze = clamp(w.fire.intensity / 700, 0, 0.62);
    const flameFrame = Math.floor(time * 14);
    const waterPhase = time * 1.7;
    const acequiaPhase = Math.floor(time * 6);
    const buf = this.buf;

    for (let y = 0; y < GRID_H; y++) {
      for (let x = 0; x < GRID_W; x++) {
        const i = y * GRID_W + x;
        const t = terrain[i];
        let colour: number;

        if (t === T.Sky) {
          const f = clamp(y / Math.max(1, mtn.ridge[x]), 0, 1);
          colour = f < 0.62 ? blend(SKY_TOP, SKY_MID, f / 0.62) : blend(SKY_MID, SKY_LOW, (f - 0.62) / 0.38);
          if (haze > 0) colour = blend(colour, SMOKE_SKY, haze * (0.35 + 0.65 * f));
          buf[i] = colour;
          continue;
        }

        if (state[i] === CellState.Burning) {
          const n = hash(i, flameFrame);
          const k = clamp(Math.floor(inten[i] * 3.2 + n * 2.6), 0, FIRE.length - 1);
          buf[i] = FIRE[k];
          continue;
        }

        if (state[i] === CellState.Burnt) {
          buf[i] = BURNT[tex[i] % BURNT.length];
          continue;
        }

        const shade = tex[i] >> 6;
        switch (t) {
          case T.Rock:
            colour = ROCK[shade];
            break;
          case T.Pine:
            colour = PINE[shade];
            break;
          case T.Scrub:
            colour = SCRUB[shade];
            break;
          case T.Olive:
            colour = y % 3 === 0 ? OLIVE_LINE : OLIVE[shade];
            break;
          case T.Grass:
            colour = GRASS[shade];
            break;
          case T.Water: {
            const wv = Math.sin(x * 0.8 + y * 2.1 + waterPhase);
            colour = WATER[wv > 0.8 ? 3 : wv > 0.05 ? 2 : 1];
            break;
          }
          case T.Acequia:
            colour = ACEQUIA[(x + acequiaPhase) % 2];
            break;
          case T.Road:
            colour = ROAD[tex[i] > 128 ? 1 : 2];
            break;
          case T.Building:
            colour = BUILDING[tex[i] >> 7];
            break;
          case T.Helipad:
          case T.Base:
            colour = PAD[tex[i] >> 7];
            break;
          default:
            colour = ROCK[shade];
            break;
        }

        // Only ground that could burn shows wet or hot: rock and roads are
        // permanently "wet" in the model and must not be tinted for it.
        const def = TERRAIN[t];
        if (def.fuel > 0) {
          // Wetted ground looks blue, because the player has to be able to see their work.
          if (moist[i] > 0.42) colour = blend(colour, BLUE, clamp((moist[i] - 0.42) * 1.15, 0, 0.6));
          // And ground that is about to catch fire looks warm, so spread is predictable.
          const th = def.ign * (1 + moist[i] * 3);
          const r = heat[i] / th;
          if (r > 0.28) colour = blend(colour, RED, Math.min(0.62, (r - 0.28) * 0.95));
        }

        buf[i] = colour;
      }
    }
  }

  private drawWindStreaks(w: World, time: number): void {
    const ctx = this.ctx;
    const { ux, uy, speed } = w.wind;
    if (speed < 0.05) return;
    ctx.globalAlpha = 0.07 + speed * 0.06;
    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = 1;
    ctx.beginPath();
    const travel = time * speed * 150;
    for (let i = 0; i < STREAKS; i++) {
      const len = this.streaks[i * 3 + 2];
      let x = this.streaks[i * 3] + ux * travel;
      let y = this.streaks[i * 3 + 1] + uy * travel;
      x = ((x % LOGICAL_W) + LOGICAL_W) % LOGICAL_W;
      y = ((y % LOGICAL_H) + LOGICAL_H) % LOGICAL_H;
      ctx.moveTo(Math.floor(x) + 0.5, Math.floor(y) + 0.5);
      ctx.lineTo(Math.floor(x + ux * len) + 0.5, Math.floor(y + uy * len) + 0.5);
    }
    ctx.stroke();
    ctx.globalAlpha = 1;
  }

  private drawPads(w: World): void {
    const pad = w.mtn.helipad;
    const base = w.mtn.base;
    this.sprite(
      PAD_MARK,
      (pad.x + pad.w / 2) * CELL - PAD_MARK.w / 2,
      (pad.y + pad.h / 2) * CELL - PAD_MARK.h / 2,
    );
    this.sprite(
      BASE_MARK,
      (base.x + base.w / 2) * CELL - BASE_MARK.w / 2,
      (base.y + base.h / 2) * CELL - BASE_MARK.h / 2,
    );
  }

  private drawHouses(w: World): void {
    const ctx = this.ctx;
    for (const h of w.mtn.houses) {
      const i = idx(h.x + 1, h.y + 1);
      const st = w.grid.state[i];
      const px = h.x * CELL;
      const py = h.y * CELL;
      if (st === CellState.Burnt) {
        this.sprite(HOUSE_BURNT, px, py);
      } else if (st === CellState.Burning) {
        ctx.globalAlpha = 0.45;
        this.sprite(HOUSE, px, py);
        ctx.globalAlpha = 1;
      } else {
        this.sprite(HOUSE, px, py);
      }
    }
  }

  private drawCars(w: World): void {
    for (const car of w.cars) {
      const road = w.roads[car.roadId];
      const c = road.cells[clamp(Math.round(car.s), 0, road.cells.length - 1)];
      const s = CARS[car.id % CARS.length];
      this.sprite(s, c.x * CELL + CELL / 2 - s.w / 2, c.y * CELL + CELL / 2 - s.h / 2);
    }
  }

  private drawEngines(w: World, time: number): void {
    const ctx = this.ctx;
    for (const e of w.engines) {
      if (e.state === EState.Responding) continue;
      const c = engineCell(w, e);
      const px = c.x * CELL + CELL / 2;
      const py = c.y * CELL + CELL / 2;
      const selected = w.selected === e.id;

      if (e.state === EState.Lost) {
        this.sprite(FIRE_ENGINE_LOST, px - FIRE_ENGINE_LOST.w / 2, py - FIRE_ENGINE_LOST.h / 2);
        continue;
      }

      // Where they have been sent, and how far the hose reaches once there.
      if (selected) {
        ctx.strokeStyle = '#00ffff';
        ctx.globalAlpha = 0.45;
        ctx.setLineDash([2, 3]);
        ctx.beginPath();
        ctx.ellipse(px, py, HOSE_REACH * CELL, HOSE_REACH * CELL * 0.85, 0, 0, Math.PI * 2);
        ctx.stroke();
        if (e.order) {
          const road = w.roads[e.order.objective.roadId];
          const t = road.cells[clamp(Math.round(e.order.objective.s), 0, road.cells.length - 1)];
          ctx.beginPath();
          ctx.moveTo(px, py);
          ctx.lineTo(t.x * CELL + 2, t.y * CELL + 2);
          ctx.stroke();
        }
        ctx.setLineDash([]);
        ctx.globalAlpha = 1;
      }

      // A working crew throws a jet at whatever they are wetting.
      if (e.state === EState.Working && e.water > 0) {
        ctx.strokeStyle = '#00ffff';
        ctx.globalAlpha = 0.5 + 0.3 * Math.sin(time * 12 + e.id);
        ctx.beginPath();
        ctx.moveTo(px, py - 2);
        ctx.lineTo(px + Math.sin(time * 2.2 + e.id) * 10, py - 8);
        ctx.stroke();
        ctx.globalAlpha = 1;
      }

      this.sprite(FIRE_ENGINE, px - FIRE_ENGINE.w / 2, py - FIRE_ENGINE.h / 2);

      // A fire engine sitting with no orders blinks, so you cannot miss that
      // there is something on the map waiting for you to use it.
      if (!e.order && e.state !== EState.Withdrawing && Math.floor(time * 2.5) % 2 === 0) {
        ctx.fillStyle = '#ffff00';
        const ax = Math.round(px);
        const ay = Math.round(py) - 9;
        ctx.fillRect(ax - 1, ay - 4, 3, 5);
        ctx.fillRect(ax - 1, ay + 2, 3, 2);
      }

      // Blue light while responding or withdrawing; red flash when threatened.
      if (e.warned && Math.floor(time * 5) % 2 === 0) {
        ctx.fillStyle = '#ff0000';
        ctx.fillRect(Math.round(px) - 1, Math.round(py) - 7, 3, 3);
      }
      if (selected) {
        ctx.strokeStyle = '#00ffff';
        ctx.strokeRect(Math.round(px) - 6.5, Math.round(py) - 5.5, 13, 10);
      }
    }
  }

  private drawPeople(w: World, time: number): void {
    const ctx = this.ctx;
    const flash = Math.floor(time * 6) % 2 === 0;
    for (const p of w.people) {
      if (p.state === PState.Home || p.state === PState.InCar) continue;
      if (p.state >= PState.Safe) continue;
      if (p.state === PState.Aboard) continue;
      let colour: string;
      if (p.health < 0.55) colour = flash ? '#ff0000' : '#ffff00';
      else if (p.state === PState.Waiting) colour = '#00ffff';
      else if (p.state === PState.Walking) colour = '#ffffff';
      else colour = '#ffff00';
      ctx.fillStyle = colour;
      ctx.fillRect(Math.round(p.x * CELL) - 1, Math.round(p.y * CELL) - 2, 2, 3);
    }
  }

  private drawDrops(w: World): void {
    const ctx = this.ctx;
    for (const d of w.drops) {
      const f = d.t / d.fall;
      const r = 2 + f * 4;
      ctx.fillStyle = '#0000ff';
      ctx.beginPath();
      ctx.ellipse(Math.round(d.x), Math.round(d.y), r, r * 1.5, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = '#00ffff';
      ctx.fillRect(Math.round(d.x) - 1, Math.round(d.y) - Math.round(r), 2, 2);
    }
  }

  private drawParticles(w: World): void {
    const ctx = this.ctx;
    for (const p of w.particles) {
      const f = p.life / p.max;
      switch (p.kind) {
        case PKind.Smoke: {
          const size = 2 + Math.round((1 - f) * 3);
          ctx.globalAlpha = f * 0.42;
          ctx.fillStyle = p.seed > 128 ? '#2a2a32' : '#3c3c46';
          ctx.fillRect(Math.round(p.x), Math.round(p.y), size, size);
          break;
        }
        case PKind.Ember:
          ctx.globalAlpha = Math.min(1, f * 1.6);
          ctx.fillStyle = p.seed > 100 ? '#ffff00' : '#ff8700';
          ctx.fillRect(Math.round(p.x), Math.round(p.y), 1, 1);
          break;
        case PKind.Splash:
          ctx.globalAlpha = f;
          ctx.fillStyle = '#00ffff';
          ctx.fillRect(Math.round(p.x), Math.round(p.y), 1, 1);
          break;
        case PKind.Steam:
          ctx.globalAlpha = f * 0.6;
          ctx.fillStyle = '#d7d7d7';
          ctx.fillRect(Math.round(p.x), Math.round(p.y), 2, 2);
          break;
        case PKind.Cheer:
          ctx.globalAlpha = f;
          ctx.fillStyle = p.seed > 128 ? '#00ff00' : '#ffff00';
          ctx.fillRect(Math.round(p.x), Math.round(p.y), 1, 1);
          break;
      }
    }
    ctx.globalAlpha = 1;
  }

  private drawHeli(w: World, time: number): void {
    const ctx = this.ctx;
    const h = w.heli;
    const x = Math.round(h.x);
    const y = Math.round(h.y);

    // Predicted impact point, so aiming a drop is a skill and not a guess.
    if (h.mode === 'bucket' && h.water > 0.05) {
      const px = h.x + (h.vx * 0.35 + w.wind.ux * w.wind.speed * DROP_DRIFT) * DROP_FALL;
      const py = h.y + 4 + (h.vy * 0.35 + w.wind.uy * w.wind.speed * DROP_DRIFT) * DROP_FALL;
      const r = w.diff.dropRadius * Math.max(0.55, Math.sqrt(h.water)) * CELL;
      ctx.strokeStyle = '#00ffff';
      ctx.globalAlpha = 0.5;
      ctx.setLineDash([2, 3]);
      ctx.beginPath();
      ctx.ellipse(Math.round(px), Math.round(py), r, r * 0.72, 0, 0, Math.PI * 2);
      ctx.stroke();
      ctx.setLineDash([]);
      ctx.globalAlpha = 1;
    }

    // Bucket on its line. Position comes from the simulation so that what looks
    // like it is dipping in the water is exactly what the scoop test uses.
    if (h.mode === 'bucket') {
      const a = bucketAnchor(h);
      const bx = Math.round(a.x);
      const by = Math.round(a.y);
      ctx.strokeStyle = '#2a2a34';
      ctx.beginPath();
      ctx.moveTo(x + 0.5, y + 3.5);
      ctx.lineTo(bx + 0.5, by - 1.5);
      ctx.stroke();
      this.sprite(BUCKET, bx - 2, by - 2);
      if (h.water > 0.02) {
        ctx.fillStyle = '#0000ff';
        const fillH = Math.max(1, Math.round(h.water * 2));
        ctx.fillRect(bx - 1, by - 1 + (2 - fillH), 2, fillH);
      }
      // A ripple whenever the bucket can actually draw water.
      if (bucketInWater(w.grid, h)) {
        ctx.strokeStyle = '#00ffff';
        ctx.globalAlpha = 0.75;
        ctx.beginPath();
        ctx.ellipse(bx, by + 2, 5, 2, 0, 0, Math.PI * 2);
        ctx.stroke();
        ctx.globalAlpha = 1;
      }
    }

    const body = h.facing < 0 ? HELI_L : HELI;
    this.sprite(body, x - body.w / 2, y - body.h / 2);

    // Skids.
    ctx.strokeStyle = '#57576e';
    ctx.beginPath();
    ctx.moveTo(x - 6.5, y + 5.5);
    ctx.lineTo(x + 5.5, y + 5.5);
    ctx.stroke();

    // Main rotor: a blurred disc, faster when airborne.
    const spin = Math.sin(h.rotor * 2.2);
    const rw = 13 + spin * 4;
    ctx.globalAlpha = h.grounded > 0 ? 0.35 : 0.7;
    ctx.strokeStyle = '#d7d7d7';
    ctx.beginPath();
    ctx.moveTo(x - rw, y - 5.5);
    ctx.lineTo(x + rw, y - 5.5);
    ctx.stroke();
    ctx.globalAlpha = 0.3;
    ctx.beginPath();
    ctx.moveTo(x - rw * 0.6, y - 4.5);
    ctx.lineTo(x + rw * 0.6, y - 4.5);
    ctx.stroke();
    ctx.globalAlpha = 1;

    // Tail rotor.
    const tx = x + (h.facing < 0 ? 8 : -8);
    const th = 2 + Math.abs(Math.cos(h.rotor * 2.6)) * 3;
    ctx.strokeStyle = '#d7d7d7';
    ctx.globalAlpha = 0.75;
    ctx.beginPath();
    ctx.moveTo(tx + 0.5, y - th);
    ctx.lineTo(tx + 0.5, y + th);
    ctx.stroke();
    ctx.globalAlpha = 1;

    if (h.grounded > 0 && Math.floor(time * 4) % 2 === 0) {
      ctx.fillStyle = '#ff0000';
      ctx.fillRect(x - 1, y - 12, 3, 3);
    }
  }

  private sprite(s: Sprite, x: number, y: number): void {
    this.ctx.drawImage(s.canvas as CanvasImageSource, Math.round(x), Math.round(y));
  }
}


